// src/contexts/SessionContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  initializeSessionFromUrl,
  hasActiveSession,
  getSession,
  createAutoSession,
  markSessionAsLoggedOut,
} from '../services/sessionService';
import type { SessionData } from '../types/session';

interface SessionContextType {
  hasSession: boolean;
  session: SessionData | null;
  logout: () => void;
  isLoading: boolean;
  isLoggedOut: boolean;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<SessionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedOut, setIsLoggedOut] = useState(false);

  useEffect(() => {
    // Initialize session on mount - auto-login behavior
    const initSession = () => {
      // Try to initialize from URL parameters first
        initializeSessionFromUrl();
      // Check a session cookie used as a logout lock across tabs in the same browser run
      const hasLogoutCookie = document.cookie.split(';').some(c => c.trim().startsWith('travreferral-logout=true'));
      // Per-tab flag still blocks within the same tab
      const logoutFlag = sessionStorage.getItem('travreferral-logout') === 'true';
      const currentSession = getSession();

      // If logout lock is present, do NOT auto-create a session
      if (hasLogoutCookie || logoutFlag) {
        setSession(null);
        setIsLoggedOut(true);
        setIsLoading(false);
        return;
      }

      // Auto-create session only if no existing session and no logout lock
      if (!currentSession) {
        const newSession = createAutoSession();
        setSession(newSession);
        setIsLoggedOut(false);
        setIsLoading(false);
        return;
      }

      setSession(currentSession);
      setIsLoggedOut(false);
      setIsLoading(false);
    };

    initSession();

    // Listen for storage changes from other tabs (cross-tab logout sync)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'travelers_session') {
        const updatedSession = getSession();
        
        // If session was logged out in another tab, update this tab
        if (updatedSession?.isLoggedOut) {
          setIsLoggedOut(true);
          setSession(updatedSession);
          } else {
          setIsLoggedOut(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const logout = () => {
    // Mark session as logged out (invalidate)
    markSessionAsLoggedOut();
    
   // Set a sessionStorage flag so refreshes in this browser session stay logged out
    sessionStorage.setItem('travreferral-logout', 'true');
    // Set a session cookie (no expires) so all tabs in current browser run see the logout lock
    document.cookie = 'travreferral-logout=true; path=/';
    setSession(null);
    setIsLoggedOut(true);

  };

  const value: SessionContextType = {
    hasSession: hasActiveSession() && !isLoggedOut,
    session,
    logout,
    isLoading,
    isLoggedOut,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};
